/**
 * Device management service
 *
 * Handles device approval and workspace seed sharing
 */

import * as deviceClient from '@yada/api-client';
import { shareWorkspaceSeedsWithDevice } from '@yada/sync';

export type Device = deviceClient.Device;

/**
 * List all devices for current profile
 */
export async function listDevices(): Promise<Device[]> {
  return deviceClient.listDevices();
}

/**
 * Approve a device and share workspace seeds
 *
 * This is the complete workflow:
 * 1. Re-wrap all workspace seeds with the new device's public key (client-side, zero-knowledge)
 * 2. Mark device as approved (server-side)
 */
export async function approveDeviceAndShareSeeds(deviceId: string): Promise<void> {
  console.log('[deviceService] Approving device and sharing seeds:', deviceId);

  try {
    // Step 1: Share workspace seeds (re-wrap with new device's key)
    // This must happen BEFORE approval so the new device can access seeds immediately
    await shareWorkspaceSeedsWithDevice(deviceId);

    // Step 2: Mark device as approved
    await deviceClient.approveDevice(deviceId);

    console.log('[deviceService] Device approved and seeds shared successfully');
  } catch (err) {
    console.error('[deviceService] Failed to approve device:', err);
    throw err;
  }
}

/**
 * Re-share workspace seeds with an already-approved device.
 *
 * Useful when a device was approved without seed sharing (e.g. via self-approval
 * before the approval flow enforced seed sharing), or when a device's IndexedDB
 * was wiped and seeds need to be re-distributed.
 *
 * Must be called from a device that can decrypt the workspace seeds.
 */
export async function reshareSeeds(deviceId: string): Promise<void> {
  console.log('[deviceService] Re-sharing seeds with device:', deviceId);
  await shareWorkspaceSeedsWithDevice(deviceId);
  console.log('[deviceService] Seeds re-shared successfully');
}

/**
 * Revoke a device
 */
export async function revokeDevice(deviceId: string): Promise<void> {
  return deviceClient.revokeDevice(deviceId);
}

/**
 * Permanently delete a device record
 */
export async function deleteDevice(deviceId: string): Promise<void> {
  return deviceClient.deleteDevice(deviceId);
}

/**
 * Get device status (for polling on pending devices)
 */
export async function getDeviceStatus(deviceId: string): Promise<{
  status: 'pending' | 'approved' | 'revoked';
  approved_at: string | null;
}> {
  return deviceClient.getDeviceStatus(deviceId);
}

/**
 * Check if current device is pending approval
 */
export async function isCurrentDevicePending(): Promise<boolean> {
  const deviceId = localStorage.getItem('yada:deviceId');
  if (!deviceId) return false;

  try {
    const status = await getDeviceStatus(deviceId);
    return status.status === 'pending';
  } catch (err) {
    console.error('[deviceService] Failed to check device status:', err);
    return false;
  }
}

/**
 * Poll for device approval
 * Returns a promise that resolves when the device is approved
 */
export async function waitForDeviceApproval(
  deviceId: string,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
  } = {}
): Promise<void> {
  const { intervalMs = 3000, timeoutMs = 300000 } = options; // Default: 3s interval, 5min timeout

  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    let isFinished = false;

    const poll = async () => {
      if (isFinished) return;

      if (Date.now() - startTime > timeoutMs) {
        isFinished = true;
        reject(new Error('Device approval timeout'));
        return;
      }

      try {
        const status = await getDeviceStatus(deviceId);

        if (status.status === 'approved') {
          isFinished = true;
          resolve();
          return;
        }

        if (status.status === 'revoked') {
          isFinished = true;
          reject(new Error('Device was revoked'));
          return;
        }
      } catch (err) {
        console.warn('[waitForDeviceApproval] Polling error (will retry):', err);
      }

      if (!isFinished) {
        setTimeout(poll, intervalMs);
      }
    };

    poll();
  });
}
